<script>
    import { onMount } from "svelte";
    import NotificationModal from "../../Components/Common/NotificationModal.svelte";
    import RichTextEditor from "../../Components/Custom/TextEditor/RichTextEditor.svelte";
    import { addErrorInput, removeErrorInput } from "../PatientEncounter/helpers/encounterUtils.svelte";
    import { apiMethods } from "../../Services/index.svelte";
    import {
        handleDownloadPDF,
        showInfo,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";
    export let selectedAgreementDocument;
    export let createEditAgreementDocumentModalOpen;
    export let reload;
    export let isViewMode = false;

    const documentTypeOptions = [
        { value: "0", label: "Terms and Conditions" },
        { value: "1", label: "Privacy Policy" },
    ];

    let fileObj = {},
        isUploadingFile = false,
        fileInput;
    let editorRef;
    let isSavingAgreementDocument = false;
    let isActiveConfirmationModalOpen = false;
    let activeConfirmationMessage = "";

    let errInputs = [];
    const agreementDocumentValidationPlaceholders = {
        agreementDocumentTypeInput: "Type",
        agreementDocumentNameInput: "Name",
        agreementDocumentVersionInput: "Version",
        agreementDocumentContentInput: "Content",
    };

    $: isEditMode = !!selectedAgreementDocument?.id;
    $: modalTitle = isViewMode
        ? "View Agreement Document"
        : `${isEditMode ? "Edit" : "Create"} Agreement Document`;
    $: submitButtonText = isViewMode
        ? ""
        : isSavingAgreementDocument
            ? "Saving"
            : isEditMode
                ? "Save Changes"
                : "Create";

    $: if (createEditAgreementDocumentModalOpen && !isViewMode)
        setTimeout(() => enableFileDragAndDrop(), 0);
    else disableFileDragAndDrop();


    onMount(() => {
        if(selectedAgreementDocument.fileContentBase64){
            fileObj = {
                name: `${selectedAgreementDocument.name}_${selectedAgreementDocument.versionNumber}.pdf`,
                content: selectedAgreementDocument.fileContentBase64
            } 

            const inputField = document.getElementById("fileNamePlaceholder");
            inputField.innerText = fileObj.name;
        }

    })

    const checkAgreementDocumentValidation = async () => {
        if (isViewMode) return;

        if (isSavingAgreementDocument) return;

        errInputs = [];

        _updateInvalidAgreementDocumentFieldsStyle();

        if (errInputs.length) return;

        _handleActiveAgreementDocumentConfirmation();
    };

    const _handleActiveAgreementDocumentConfirmation = () => {
        const isActive = !!selectedAgreementDocument?.active;

        if (isActive) {
            activeConfirmationMessage = selectedAgreementDocument?.id
                ? "Are you sure you want to update the document details? This document is marked as active, meaning that any change will trigger agreement collection from all the doctors"
                : "Are you sure you want to save this new document as active, this will trigger agreement collection from all the doctors";

            isActiveConfirmationModalOpen = true;
            return;
        }

        createEditAgreementDocument();
    };

    const confirmActiveDocumentSave = () => {
        isActiveConfirmationModalOpen = false;
        activeConfirmationMessage = "";
        createEditAgreementDocument();
    };

    const cancelActiveDocumentSave = () => {
        isActiveConfirmationModalOpen = false;
        activeConfirmationMessage = "";
    };

    const createEditAgreementDocument = async () => {
        isSavingAgreementDocument = true;

        try {
            const payload = {
                ...selectedAgreementDocument,
                documentType: Number(selectedAgreementDocument.documentType),
                fileContentBase64: fileObj.content ?? null,
            };

            if (selectedAgreementDocument.id) {
                await apiMethods.putMethod
                    .editAgreementDocument(payload)
                    .then(async (res) => {
                        if (!res || res.error)
                            showError(
                                "Failed to edit agreement document, please refresh the page and try again and contact support if the issue persists.",
                            );
                        else await reload();
                    })
                    .catch((err) => showException(err));
            } else {
                await apiMethods.postMethod
                    .createAgreementDocument(payload)
                    .then(async (res) => {
                        if (!res || res.error)
                            showError(
                                "Failed to create agreement document, please refresh the page and try again and contact support if the issue persists.",
                            );
                        else await reload();
                    })
                    .catch((err) => showException(err));
            }

            _closeCreateEditAgreementDocumentModal();
        } finally {
            isSavingAgreementDocument = false;
        }
    };

    const _updateInvalidAgreementDocumentFieldsStyle = () => {
        if (!_isValidDocumentType(selectedAgreementDocument.documentType))
            errInputs = addErrorInput(
                "agreementDocumentTypeInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentTypeInput,
            );
        else
            errInputs = removeErrorInput(
                "agreementDocumentTypeInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentTypeInput,
            );

        if (!_hasTextValue(selectedAgreementDocument.name))
            errInputs = addErrorInput(
                "agreementDocumentNameInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentNameInput,
            );
        else
            errInputs = removeErrorInput(
                "agreementDocumentNameInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentNameInput,
            );

        if (!_hasTextValue(selectedAgreementDocument.versionNumber))
            errInputs = addErrorInput(
                "agreementDocumentVersionInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentVersionInput,
            );
        else
            errInputs = removeErrorInput(
                "agreementDocumentVersionInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentVersionInput,
            );

        if (!_hasTextValue(selectedAgreementDocument.content))
            errInputs = addErrorInput(
                "agreementDocumentContentInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentContentInput,
            );
        else
            errInputs = removeErrorInput(
                "agreementDocumentContentInput",
                errInputs,
                agreementDocumentValidationPlaceholders.agreementDocumentContentInput,
            );
    };

    const _resetAgreementDocumentValidationState = () => {
        const currentErrInputs = [...errInputs];

        currentErrInputs.forEach((id) => {
            errInputs = removeErrorInput(
                id,
                errInputs,
                agreementDocumentValidationPlaceholders[id] ?? "",
            );
        });

        errInputs = [];
    };

    const _closeCreateEditAgreementDocumentModal = () => {
        createEditAgreementDocumentModalOpen = false;
        isActiveConfirmationModalOpen = false;
        activeConfirmationMessage = "";
        _resetAgreementDocumentValidationState();
        fileObj = {};
        selectedAgreementDocument = {};
    };

    const _hasTextValue = (value) =>
        typeof value === "string"
            ? value.trim().length > 0
            : value !== undefined && value !== null && value !== "";

    const _isValidDocumentType = (value) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        documentTypeOptions.some((option) => option.value === value.toString());

    const _handleFileChange = (e, isDropFile = false) => {
        if (isViewMode) return;

        if (isUploadingFile) return;

        const inputField = document.getElementById("fileNamePlaceholder");

        inputField.innerText = isDropFile
            ? e.dataTransfer.files[0].name
            : e.target.files[0].name;

        if(selectedAgreementDocument.name && selectedAgreementDocument.versionNumber){
            inputField.innerText = `${selectedAgreementDocument.name}_${selectedAgreementDocument.versionNumber}.pdf`;
        }

        const file = isDropFile ? e.dataTransfer.files[0] : e.target.files[0];
        const fileType = file?.type;
        const fileName = file?.name;

        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            fileObj = {
                name: fileName,
                type: fileType,
                content: e.target.result.split(",")[1],
            };
        };

        showInfo("File uploaded successfully, please don't forget to save the changes.");
    };

    const enableFileDragAndDrop = () => {
        const enableDropArea = document.getElementById("enableDropArea");
        const uploadFileModal = document.getElementById("uploadFileModal");

        uploadFileModal.addEventListener("dragover", function (e) {
            e.preventDefault();

            enableDropArea.classList.add("outline-dashed");
            enableDropArea.classList.add("outline-secondary");
            enableDropArea.classList.add("outline-2");
        });

        uploadFileModal.addEventListener("dragleave", function (e) {
            enableDropArea.classList.remove("outline-dashed");
            enableDropArea.classList.remove("outline-secondary");
            enableDropArea.classList.remove("outline-2");
        });

        uploadFileModal.addEventListener("drop", function (e) {
            e.preventDefault();

            enableDropArea.classList.remove("outline-dashed");
            enableDropArea.classList.remove("outline-secondary");
            enableDropArea.classList.remove("outline-2");
        });

        enableDropArea.addEventListener("drop", function (e) {
            e.preventDefault();

            if (isUploadingFile) return;

            _handleFileChange(e, true);

            this.classList.remove("outline-dashed");
            this.classList.remove("outline-secondary");
            this.classList.remove("outline-2");
        });
    };

    const disableFileDragAndDrop = () => {
        const enableDropArea = document.getElementById("enableDropArea");

        enableDropArea &&
            enableDropArea.removeEventListener("drop", _handleFileChange);
    };

    const _handleDocumentDownload = () => {
        handleDownloadPDF(fileObj.content, fileObj.name ?? `${selectedAgreementDocument.name}_${selectedAgreementDocument.versionNumber}.pdf`, true);
    }

</script>

<NotificationModal
    title={modalTitle}
    submitText={submitButtonText}
    isModalOpen={createEditAgreementDocumentModalOpen}
    isConfirming={isSavingAgreementDocument}
    closeModal={_closeCreateEditAgreementDocumentModal}
    confirmAction={checkAgreementDocumentValidation}
    closeModalFunction={_closeCreateEditAgreementDocumentModal}
    showConfirmBtn={!isViewMode}
    noContentPadding={true}
    modalMaxWidth="max-w-6xl"
>
    <div class="flex flex-col gap-y-4 p-8">
        <div class="flex flex-row gap-x-4">
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Type:</p>
                <div class="w-remaining pl-1">
                    <select
                        id="agreementDocumentTypeInput"
                        bind:value={selectedAgreementDocument.documentType}
                        class="select select-bordered select-sm w-full"
                        disabled={isViewMode}
                    >
                        <option value="">Select type</option>
                        {#each documentTypeOptions as option}
                            <option value={option.value}>{option.label}</option>
                        {/each}
                    </select>
                </div>
            </div>
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Active:</p> 
                <div class="w-remaining">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={selectedAgreementDocument.active}
                        disabled={isViewMode}
                    />
                </div>
            </div>
        </div>
        <div class="flex flex-row gap-x-4">
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Name:</p>
                <div class="w-remaining pl-1">
                    <input
                        type="text"
                        id="agreementDocumentNameInput"
                        bind:value={selectedAgreementDocument.name}
                        class="input input-bordered input-sm w-full"
                        autocomplete="off"
                        placeholder="Name"
                        disabled={isViewMode}
                    />
                </div>
            </div>
            <div class="flex-1 flex gap-x-4 items-center">
                <p class="title">Version:</p>
                <input
                    type="text"
                    id="agreementDocumentVersionInput"
                    bind:value={selectedAgreementDocument.versionNumber}
                    class="input input-bordered input-sm w-remaining"
                    autocomplete="off"
                    placeholder="Version"
                    disabled={isViewMode}
                />
            </div>
        </div>
        <div class="flex flex-row gap-x-4" id="uploadFileModal">
            <div class="title">
                PDF file:
            </div>
            <div class="flex flex-col w-remaining gap-y-2 pl-1" id="enableDropArea">
                <div class="flex flex-1 text-[#000]">
                    <p
                        id="fileNamePlaceholder"
                        class="w-1/2 pt-2 pl-2 file-name rounded-tl-lg rounded-bl-lg text-secondary"
                    />
                    <button
                        class="w-1/2 p-2 rounded-tr-lg rounded-br-lg bg-[#E8E8E8] text-secondary {isUploadingFile || isViewMode
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer'} {isViewMode ? 'opacity-60' : ''}"
                        disabled={isViewMode}
                        on:click|preventDefault={() =>
                            !isViewMode && !isUploadingFile && fileInput.click()}
                    >
                        Select or drag and drop here
                    </button>
                    <input
                        type="file"
                        accept=".pdf"
                        style="display: none;"
                        id="fileInput"
                        on:change={_handleFileChange}
                        bind:this={fileInput}
                        disabled={isViewMode}
                    />
                </div>
            </div>
            {#if selectedAgreementDocument.fileContentBase64}
                 <button
                        class="break-all "
                        on:click|preventDefault={() =>
                        _handleDocumentDownload()}
                >
                    <i class="downloadIcon fas fa-download"></i>
                </button>
            {/if}
        </div>
        
        <div class="flex gap-x-4 items-start h-[600px]">
            <p class="title mt-2">Content:</p>
            <div class="flex flex-1 w-remaining h-full content-editor">
                <RichTextEditor
                    bind:content={selectedAgreementDocument.content}
                    bind:this={editorRef}
                    config={{ isDisabled: isViewMode }}
                />
            </div>
        </div>
      
    </div>
</NotificationModal>

<NotificationModal
    title="Confirm Action"
    isModalOpen={isActiveConfirmationModalOpen}
    closeModal={cancelActiveDocumentSave}
    closeModalFunction={cancelActiveDocumentSave}
    confirmAction={confirmActiveDocumentSave}
    submitText="Yes"
    cancelText="No"
    isReverseBtns
>
    {activeConfirmationMessage}
</NotificationModal>

<style>
    .title {
        width: 170px;
    }

    .w-remaining {
        width: calc(100% - 170px);
    }

    .content-editor {
        height: 100%;
        min-height: 100%;
        overflow-y: auto;
    }

    .content-editor :global(.ql-editor) {
        width: 100%;
        height: 100%;
        overflow-y: auto;
    }

    .content-editor :global(.ql-container.ql-disabled),
    .content-editor :global(.ql-container.ql-disabled .ql-editor) {
        pointer-events: auto;
        cursor: default;
    }

    .file-name {
        border-width: 1px;
        border-color: #cecece;
        padding-left:10px;
    }
</style>
